import CustomTable from "@Common/CustomTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CardsInfo from "./cardsInfo";
import { GuestManagementWrapper } from "./styles";
import GuestMangmentHeader from "./guestMangmentHeader";
import {
	useGetGuestManagementClassification,
	useGuestManagement,
} from "@services"
import SelectClassfication from "./selectClassfication";
import PopupReservationsList from "./popupReservationsList";
import { GustReservation } from "src/services/useGuestManagement/types";
import PopupConfirmation from "./popupConfirmation";
import { CustomNoRowsOverlay } from "./CustomNoRowsOverlay";
import { ReservationFiltersState } from "../ReservationManagement/ReservationFilters";

export default function GuestManagement() {
  const [classficationPopup, setClassficationPopup] = useState(false);

  const [reservationsList, setReservationsList] = useState<GustReservation[]>(
    []
  );
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [perPage, onPerpageChange] = useState("20");
  const [activePage, setActivePage] = useState(1);

  // Add filter state management
  const [filters, setFilters] = useState<ReservationFiltersState>({
    dateRange: null,
    selectedChannels: [],
    selectedNames: [],
    priceRange: [0, 1000],
    enum: [],
  });

  // Add name search term state
  const [nameSearchTerm, setNameSearchTerm] = useState("");

  const { GuestsQuery } = useGuestManagement();
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { data: classificationQuery } = useGetGuestManagementClassification({
    lang: language === "ar" ? "A" : "E",
  })

  // Convert filter state to API parameters
  const apiParams = useMemo(() => {
    const params: any = {
      pageNumber: activePage.toString(),
      pageSize: perPage,
    };

    // Add selected names if available
    if (filters.selectedNames && filters.selectedNames.length > 0) {
      params.selectedNames = filters.selectedNames;
    }

    // Add name search term if available
    if (nameSearchTerm && nameSearchTerm.trim() !== "") {
      params.searchQuery = nameSearchTerm.trim();
    }

    // Add category if available (using enum filter for classification)
    if (filters.enum && filters.enum.length > 0) {
      params.category = filters.enum[0]; // Assuming single category selection
    }

    return params;
  }, [activePage, perPage, filters, nameSearchTerm]);

  const { data: Guests, isLoading } = GuestsQuery(apiParams);

  const handleFiltersChange = (newFilters: ReservationFiltersState) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setActivePage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: null,
      selectedChannels: [],
      selectedNames: [],
      priceRange: [0, 1000],
      enum: [],
    });
    setNameSearchTerm(""); // Clear name search term to empty string
    setActivePage(1);
  };

  // Handle name search term change
  const handleNameSearchChange = (searchTerm: string) => {
    setNameSearchTerm(searchTerm);
    // Reset to first page when search term changes
    setActivePage(1);
  };

  const columns = [
    {
      field: "name",
      headerName: t("GuestManagement.Name"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.name}
        </p>
      ),
    },
    {
      field: "classification",
      headerName: t("GuestManagement.classification"),
      flex: 1,
      renderCell: (params: any) => (
        <SelectClassfication
          data={params.row.classification}
          guestName={params.row.name}
          setClassficationPopup={setClassficationPopup}
        />
      ),
    },
    {
      field: "numberOfTotalVisit",
      headerName: t("GuestManagement.NumVisits"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.numberOfTotalVisit}
        </p>
      ),
    },
    {
      field: "phoneNumber",
      headerName: t("GuestManagement.phoneNumber"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.phoneNumber}
        </p>
      ),
    },
    {
      field: "visitedProperties",
      headerName: t("GuestManagement.Area"),
      flex: 1,
      renderCell: (params: any) => (
        <p style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}>
          {params?.row?.reservations?.[0]?.propertyLocation ??
            params.row.visitedProperties}
        </p>
      ),
    },
    {
      field: "reservations",
      headerName: t("GuestManagement.PropertyReservations"),
      flex: 1,
      renderCell: (params: any) => (
        <div className="containerPopupAction">
          <p
            className="columnsContent"
            style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
            onClick={() => {
              setReservationsList(params.row.reservations);
              setIsPopupOpen(true);
            }}
          >
            {t("GuestManagement.Show")}
          </p>
        </div>
      ),
    },
  ];

  const rows = useMemo(() => {
    const data = Guests?.data?.result?.guests?.map((item, index) => {
      return {
        id: index,
        name: item.name,
        classification: item.classification,
        numberOfTotalVisit: item.numberOfTotalVisit,
        phoneNumber: item.phoneNumber,
        visitedProperties: item.visitedProperties,
        reservations: item.reservations,
      };
    });
    return data;
  }, [Guests]);

  const data = {
    blacklistedGuests: Guests?.data?.result?.blacklistedGuests,
    repeatedGuests: Guests?.data?.result?.repeatedGuests,
    totalGuests: Guests?.data?.result?.totalGuests,
  };
  return (
    <GuestManagementWrapper>
      {isPopupOpen && (
        <PopupReservationsList
          reservationsList={reservationsList}
          setIsPopupOpen={setIsPopupOpen}
        />
      )}
      {classficationPopup && (
        <PopupConfirmation setClassficationPopup={setClassficationPopup} />
      )}
      <GuestMangmentHeader />
      <CardsInfo data={data} />
      <div className="containerTable">
        <CustomTable
          loading={isLoading}
          columns={columns}
          rows={rows ?? []}
          HeaderTheTable={t("GuestManagement.GuestList")}
          totalPages={
            Guests?.data?.totalRecords
              ? Math.ceil(Guests?.data?.totalRecords / Number(perPage))
              : 1
          }
          totalRecords={Guests?.data?.totalRecords}
          setActivePage={setActivePage}
          activePage={activePage}
          onPerpageChange={onPerpageChange}
          perPage={perPage}
          CustomNoRowsOverlay={CustomNoRowsOverlay}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onNameSearchChange={handleNameSearchChange}
          visibleFilters={["name", "enum"]}
          enumOptions={classificationQuery?.result.map(t => t.name)}
          listPerPagesNumber={[
            {
              label: "20",
              value: "20",
            },
            {
              label: "50",
              value: "50",
            },
            {
              label: "100",
              value: "100",
            },
          ]}
          nameFilterPlaceholder={t("GuestManagement.name")}
          EnumFilterLabel={t("GuestManagement.classification")}
        />
      </div>
    </GuestManagementWrapper>
  );
}
